import type { VerdaInstanceType, VerdaProductLink } from '../../../types/models/Verda';
import { formatCurrencyAmount } from '../../../utils/money';

// Pure helpers behind the Instance types tab (filtering, sorting, cell labels), kept out of the
// component so they can be unit-tested.

export type VerdaInstanceKind = 'all' | 'gpu' | 'cpu';

/**
 * `'cpu'` for a CPU-only node (`gpu.count === 0`), `'gpu'` for a positive count, `null` when Verda
 * didn't say — such a row matches neither kind filter and only shows under "All".
 */
export function instanceKind(type: VerdaInstanceType): 'gpu' | 'cpu' | null {
  if (type.gpu.count === 0) {
    return 'cpu';
  }
  return type.gpu.count !== null && type.gpu.count > 0 ? 'gpu' : null;
}

/** Confidential-computing variants are marked by a `.CC` suffix on the instance-type code. */
export function isConfidential(type: VerdaInstanceType): boolean {
  return type.instance_type.endsWith('.CC');
}

/**
 * The shared search + kind filter: a case-insensitive match on the code, name and model (an empty
 * query matches all). `type` is `null` for a code missing from the catalog, which then matches on its
 * code only and shows under "All" only.
 */
export function matchesInstanceFilters(
  code: string,
  type: VerdaInstanceType | null,
  query: string,
  kind: VerdaInstanceKind,
): boolean {
  if (kind !== 'all' && (type === null || instanceKind(type) !== kind)) {
    return false;
  }
  const q = query.trim().toLowerCase();
  return !q || [code, type?.name ?? null, type?.model ?? null].some((field) => field?.toLowerCase().includes(q));
}

export function filterInstanceTypes(
  types: VerdaInstanceType[],
  query: string,
  kind: VerdaInstanceKind,
): VerdaInstanceType[] {
  return types.filter((t) => matchesInstanceFilters(t.instance_type, t, query, kind));
}

export type VerdaSortKey = 'vram' | 'vcpu' | 'ram' | 'on_demand' | 'spot';

// Numeric sort keys. Prices go through Number() for ordering only (display, never stored or sent).
const SORT_VALUE: Record<VerdaSortKey, (t: VerdaInstanceType) => number | null> = {
  vram: (t) => t.gpu.memory_gb,
  vcpu: (t) => t.cpu.cores,
  ram: (t) => t.memory.size_gb,
  on_demand: (t) => Number(t.prices.on_demand_per_hour),
  spot: (t) => Number(t.prices.spot_per_hour),
};

/**
 * Returns a sorted copy (or `types` itself when `key` is `null`, i.e. Verda's order). Unknown values
 * sort last in both directions; ties keep Verda's order (Array#sort is stable).
 */
export function sortInstanceTypes(
  types: VerdaInstanceType[],
  key: VerdaSortKey | null,
  direction: 'asc' | 'desc',
): VerdaInstanceType[] {
  if (key === null) {
    return types;
  }
  const value = SORT_VALUE[key];
  const sign = direction === 'desc' ? -1 : 1;
  return [...types].sort((a, b) => {
    const va = value(a);
    const vb = value(b);
    const aUnknown = va === null || Number.isNaN(va);
    const bUnknown = vb === null || Number.isNaN(vb);
    if (aUnknown || bUnknown) {
      return aUnknown === bUnknown ? 0 : aUnknown ? 1 : -1;
    }
    return (va - vb) * sign;
  });
}

/** "8× H100 SXM5 80GB"; "—" for a CPU-only node or when Verda gave nothing to build it from. */
export function gpuLabel(type: VerdaInstanceType): string {
  if (type.gpu.count === 0) {
    return '—';
  }
  if (type.gpu.count !== null && type.name !== null) {
    return `${type.gpu.count}× ${type.name}`;
  }
  return type.gpu.description ?? '—';
}

/** "225 GB" for fixed local storage, else Verda's description ("dynamic"), else "—". */
export function storageLabel(type: VerdaInstanceType): string {
  if (type.storage.size_gb !== null) {
    return `${type.storage.size_gb} GB`;
  }
  return type.storage.description ?? '—';
}

/** A serverless price, or "—" when absent or `"0"` (presumably not offered serverless). */
export function serverlessPriceLabel(price: string | null, currency: string): string {
  return price === null || Number(price) === 0 ? '—' : formatCurrencyAmount(price, currency);
}

/**
 * Product links whose instance type is no longer in Verda's live catalog (Verda retired it). The tab's
 * rows come from the catalog, so these products would otherwise be invisible there. Link order is kept.
 */
export function retiredTypeLinks(links: VerdaProductLink[], catalog: VerdaInstanceType[]): VerdaProductLink[] {
  const live = new Set(catalog.map((t) => t.instance_type));
  return links.filter((link) => !live.has(link.instance_type));
}
